import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React from 'react';
import {useToasts} from 'react-toast-notifications';

import {useMyOan} from '../../../api/hooks/useMyOan.hook';
import {SocialNetworksEnum} from '../../../utils/_enums/social-networks.enum';
import {StrapiHeadlineElements} from '../../../utils/_enums/strapi-headline-elements.enum';
import {StrapiHeadlineStyles} from '../../../utils/_enums/strapi-headline-styles.enum';
import {ToasterTypes} from '../../../utils/_enums/toaster-types.enum';
import {TypographyVariantsEnum} from '../../../utils/_enums/typeography-variants.enum';
import {mapStrapiHeadline} from '../../../utils/_helper/strapi-mapper';
import truncateString from '../../../utils/_helper/truncate-string-helper';
import {Button} from '../../_shared/button/button';
import {
    Props as StageImageButtonTokenProps,
    StageImageButtonToken
} from '../../_shared/stage/stage-image-button-token/stage-image-button-token';
import {Typography} from '../../_shared/typography/typography';
import styles from './my-oan-stage.module.scss';

interface MyOanStageProps {
    username: string;
    description: string;
    inverted?: boolean;
    background?: string;
    decButton: { image: string };
    socialNetworks?: {
        id: number;
        socialNetwork: SocialNetworksEnum;
        url: string;
    }[];
    walletId?: string;
    titleVariant?: StrapiHeadlineStyles;
    titleElement?: StrapiHeadlineElements;
}

const MyOanStage: React.FC<MyOanStageProps> = ({
                                                   username,
                                                   description,
                                                   inverted,
                                                   background,
                                                   decButton,
                                                   socialNetworks,
                                                   walletId,
                                                   titleElement,
                                                   titleVariant
                                               }): JSX.Element => {
    const data = useMyOan();
    const {addToast} = useToasts();

    const copyWalletAddress = () => {
        if (walletId) {
            const ta = document.createElement('textarea');
            ta.innerText = walletId;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            ta.remove();
            addToast(data.copiedSuccessfully, {
                appearance: ToasterTypes.SUCCESS
            });
        }
    };

    const getSocialNetworkIcon = (socialNetwork: SocialNetworksEnum): React.ReactNode => {
        switch (socialNetwork) {
            case SocialNetworksEnum.FACEBOOK:
                return <FontAwesomeIcon icon={['fab', 'facebook-f']} className={styles['oan-icon-facebook']}/>
            case SocialNetworksEnum.INSTAGRAM:
                return <FontAwesomeIcon icon={['fab', 'instagram']} className={styles['oan-icon']}/>
            case SocialNetworksEnum.TWITTER:
                return <FontAwesomeIcon icon={['fab', 'twitter']} className={styles['oan-icon']}/>
        }
    }

    return (
        <div className={`${styles['stage']} ${styles['Small']}`}>
            <div
                style={{
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'top right'
                }}
            >
                <div
                    className={`${inverted ? styles['inverted'] : ''} ${
                        styles['CENTER']
                    }`}
                >
                    <div>
                        <div className={styles['title']}>
                            <Typography
                                disableMarkdown
                                variant={
                                    titleVariant
                                        ? mapStrapiHeadline(titleVariant)
                                        : TypographyVariantsEnum.HEADING1
                                }
                                element={titleElement ? titleElement : 'h1'}
                            >
                                {username ? data?.stage?.headline?.headlineContent + ' @' + username : ''}
                            </Typography>
                        </div>
                        <div className={styles['description']}>
                            <Typography
                                variant={TypographyVariantsEnum.INTROTEXT}
                            >
                                {description}
                            </Typography>
                        </div>
                    </div>
                    <div className={styles['social-network-buttons']}>
                        {socialNetworks?.map((socialNetwork) => {
                            if (socialNetwork.url) {
                                return (
                                    <a
                                        target={'_blank'}
                                        href={socialNetwork.url}
                                        rel={'noopener noreferrer'}
                                        className={
                                            styles['social-network-link']
                                        }
                                    >
                                        <Button
                                            key={socialNetwork.id}
                                            className={
                                                styles['social-network-button']
                                            }
                                            icon={{
                                                element: getSocialNetworkIcon(
                                                    socialNetwork.socialNetwork
                                                ),
                                                align: 'left'
                                            }}
                                        />
                                    </a>
                                );
                            }
                        })}
                    </div>
                </div>
                <div>
                    <div className={styles['decButtonContainer']}>
                        <StageImageButtonToken
                            {...(decButton as StageImageButtonTokenProps)}
                            image={decButton.image}
                            shadow
                        />
                    </div>
                    {walletId ? (
                        <div className={styles['wallet-address-wrapper']}>
                            <Typography
                                disableMarkdown
                                variant={TypographyVariantsEnum.CAPTION}
                                element={'span'}
                            >
                                {data.walletId}
                            </Typography>
                            <div className={styles['wallet-address']}>
                                <Typography>
                                    {truncateString(walletId, 6, 4)}
                                </Typography>
                                <div className={styles['copy-icon-wrapper']}>
                                    <FontAwesomeIcon
                                        icon={['far', 'copy']}
                                        onClick={copyWalletAddress}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : undefined}
                </div>
            </div>
        </div>
    );
};

export default MyOanStage;
